import { el } from "./dom";
import { createSelectorControl } from "./SelectorControl";

/**
 * Settings tab identifier. Shared across all games so the tab strip's
 * taxonomy stays consistent (Gameplay = rules/AI/AFK; Interface = theme/
 * language/name/view toggles; Audio = sound).
 */
export type SettingsTab = "gameplay" | "interface" | "audio";

export interface SettingsTabSpec {
  readonly id: SettingsTab;
  readonly label: string;
}

/**
 * One row in a settings list. Each app builds its own row set and supplies
 * fresh row defs on every render — the panel only handles layout + change
 * routing, not the underlying settings state.
 *
 * `tab` (optional) buckets the row into a tab when the panel is configured
 * with a `tabs` array. Rows without a `tab` are shown only when no tabs are
 * configured (legacy callers).
 *
 * `disabled` (toggle + selector) renders the row visually muted and skips the
 * onChange/onClick handlers. The pause-menu uses this to lock game-config
 * rows during online matches.
 */
export type SettingsRow =
  | {
      readonly kind: "text";
      readonly key: string;
      readonly label: string;
      readonly value: string;
      readonly onClick: () => void;
      readonly tab?: SettingsTab;
    }
  | {
      readonly kind: "toggle";
      readonly key: string;
      readonly label: string;
      readonly value: boolean;
      readonly onClick: () => void;
      readonly disabled?: boolean;
      readonly tab?: SettingsTab;
    }
  | {
      readonly kind: "selector";
      readonly key: string;
      readonly label: string;
      readonly value: string;
      readonly options: readonly string[];
      readonly format: (value: string) => string;
      readonly onChange: (next: string) => void;
      readonly disabled?: boolean;
      readonly tab?: SettingsTab;
    };

export interface SettingsPanelOptions {
  /**
   * Returns the current row set. Called on mount and again after every row
   * activation so consumers can return rows that already reflect the new
   * settings state (no internal state in the panel).
   */
  readonly getRows: () => readonly SettingsRow[];
  /**
   * When provided, the panel renders a horizontal tab strip and shows only
   * rows whose `tab` matches the active tab. The first entry is the default
   * active tab; the active tab is remembered for the lifetime of the panel
   * instance (across rerenders triggered by row clicks). When omitted, the
   * panel renders every row in order — legacy behavior.
   */
  readonly tabs?: readonly SettingsTabSpec[];
}

export interface SettingsPanelHandle {
  readonly element: HTMLElement;
  /** Manually re-read rows + repaint. Useful when an outside event changes settings. */
  rerender(): void;
}

let tabIdSeq = 0;

export function createSettingsPanel(opts: SettingsPanelOptions): SettingsPanelHandle {
  const container = el("div", { class: "settings-panel" });
  const tabBar = el("div", {
    class: "settings-tabs",
    attrs: { role: "tablist" },
  });
  const list = el("div", { class: "settings-list" });

  const tabs = opts.tabs ?? [];
  const hasTabs = tabs.length > 0;
  let activeTab: SettingsTab | null = hasTabs ? tabs[0]!.id : null;
  // Stable ids per panel so aria-controls bindings survive rerenders. The
  // panel rebuilds inner DOM on every rerender, so we attach the panelId to
  // the list container (also stable) and reuse it across renders.
  const panelGroupId = `bn-settings-${++tabIdSeq}`;
  const tabIdFor = (id: SettingsTab): string => `${panelGroupId}-tab-${id}`;
  const listId = `${panelGroupId}-panel`;
  list.id = listId;

  if (hasTabs) container.appendChild(tabBar);
  container.appendChild(list);

  function renderTabBar(): void {
    while (tabBar.firstChild) tabBar.removeChild(tabBar.firstChild);
    tabs.forEach((spec) => {
      const isActive = spec.id === activeTab;
      const cls = isActive ? "settings-tab settings-tab--active" : "settings-tab";
      const btn = el("button", {
        class: cls,
        text: spec.label,
        attrs: {
          type: "button",
          role: "tab",
          id: tabIdFor(spec.id),
          "aria-selected": isActive ? "true" : "false",
          "aria-controls": listId,
          // Roving tabindex: only the active tab is in the Tab order.
          tabindex: isActive ? "0" : "-1",
        },
        on: {
          click: () => activateTab(spec.id, /* focus */ false),
          keydown: (ev: Event) => onTabKeydown(ev as KeyboardEvent, spec.id),
        },
      });
      tabBar.appendChild(btn);
    });
    list.setAttribute("role", "tabpanel");
    if (activeTab !== null) list.setAttribute("aria-labelledby", tabIdFor(activeTab));
  }

  function activateTab(id: SettingsTab, focus: boolean): void {
    if (activeTab === id) {
      if (focus) focusTabButton(id);
      return;
    }
    activeTab = id;
    rerender();
    if (focus) focusTabButton(id);
  }

  function focusTabButton(id: SettingsTab): void {
    const btn = tabBar.querySelector<HTMLButtonElement>(`#${cssEscape(tabIdFor(id))}`);
    btn?.focus();
  }

  function onTabKeydown(ev: KeyboardEvent, currentId: SettingsTab): void {
    if (!hasTabs) return;
    const idx = tabs.findIndex((t) => t.id === currentId);
    if (idx < 0) return;
    let nextIdx: number | null = null;
    switch (ev.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIdx = (idx + 1) % tabs.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIdx = (idx - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        nextIdx = 0;
        break;
      case "End":
        nextIdx = tabs.length - 1;
        break;
      default:
        return;
    }
    ev.preventDefault();
    if (nextIdx !== null) activateTab(tabs[nextIdx]!.id, /* focus */ true);
  }

  function rerender(): void {
    // Capture which control inside the panel had focus (if any) so we can
    // restore it after the rebuild — otherwise activating a row via
    // Enter/Space/Arrow or clicking a selector arrow throws the user back
    // to square one of the Tab order. We track:
    //  - rowKey: which settings row contained the focus
    //  - target: where inside the row (the row itself, or a specific
    //    selector arrow — those are the only descendants in the Tab order)
    const active = document.activeElement;
    let focusedKey: string | null = null;
    let focusedTarget: "row" | "arrow-prev" | "arrow-next" = "row";
    if (active instanceof HTMLElement && list.contains(active)) {
      const ownerRow = active.closest<HTMLElement>(".settings-row");
      focusedKey = ownerRow?.dataset.rowKey ?? null;
      if (active !== ownerRow) {
        const arrowDir = active.getAttribute("data-selector-arrow");
        if (arrowDir === "prev") focusedTarget = "arrow-prev";
        else if (arrowDir === "next") focusedTarget = "arrow-next";
      }
    }
    if (hasTabs) renderTabBar();
    while (list.firstChild) list.removeChild(list.firstChild);
    const allRows = opts.getRows();
    const visible = hasTabs ? allRows.filter((r) => r.tab === activeTab) : allRows;
    visible.forEach((row, i) => {
      list.appendChild(buildRow(row, i === 0, rerender));
    });
    if (focusedKey !== null) {
      const restoredRow = list.querySelector<HTMLElement>(
        `.settings-row[data-row-key="${cssEscape(focusedKey)}"]`,
      );
      if (restoredRow !== null) {
        if (focusedTarget === "arrow-prev" || focusedTarget === "arrow-next") {
          const dir = focusedTarget === "arrow-prev" ? "prev" : "next";
          const arrow = restoredRow.querySelector<HTMLElement>(
            `[data-selector-arrow="${dir}"]`,
          );
          if (arrow !== null) {
            arrow.focus();
            return;
          }
        }
        if (restoredRow.tabIndex !== -1) restoredRow.focus();
      }
    }
  }

  rerender();
  return { element: container, rerender };
}

function buildRow(row: SettingsRow, isFirst: boolean, rerender: () => void): HTMLElement {
  const labelNode = el("div", { class: "settings-row__label", text: row.label });
  const isDisabled =
    (row.kind === "toggle" || row.kind === "selector") && row.disabled === true;

  let valueNode: HTMLElement;
  let activate: (() => void) | undefined;
  let keyHandler: ((ev: KeyboardEvent) => void) | undefined;
  const rowAttrs: Record<string, string> = {};

  switch (row.kind) {
    case "text": {
      valueNode = el("div", { class: "row__name", text: row.value });
      activate = () => {
        row.onClick();
        rerender();
      };
      rowAttrs.role = "button";
      rowAttrs.tabindex = "0";
      break;
    }
    case "toggle": {
      valueNode = createTogglePill(row.value);
      if (!isDisabled) {
        activate = () => {
          row.onClick();
          rerender();
        };
        rowAttrs.role = "switch";
        rowAttrs["aria-checked"] = row.value ? "true" : "false";
        rowAttrs.tabindex = "0";
      } else {
        rowAttrs.role = "switch";
        rowAttrs["aria-checked"] = row.value ? "true" : "false";
        rowAttrs["aria-disabled"] = "true";
      }
      break;
    }
    case "selector": {
      valueNode = createSelectorControl<string>({
        label: row.label,
        value: row.value,
        options: row.options,
        format: row.format,
        onChange: (next) => {
          if (isDisabled) return;
          row.onChange(next);
          rerender();
        },
      });
      if (!isDisabled) {
        // Row-level Arrow keys mirror the ◀▶ buttons so keyboard users don't
        // need to tab into the per-arrow controls. Wrap-around to match the
        // selector's own behavior.
        rowAttrs.tabindex = "0";
        keyHandler = (ev: KeyboardEvent) => {
          if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;
          const len = row.options.length;
          if (len === 0) return;
          const idx = Math.max(0, row.options.indexOf(row.value));
          const delta = ev.key === "ArrowRight" ? 1 : -1;
          const next = row.options[(idx + delta + len) % len];
          if (next !== undefined && next !== row.value) {
            ev.preventDefault();
            row.onChange(next);
            rerender();
          }
        };
      } else {
        rowAttrs["aria-disabled"] = "true";
      }
      break;
    }
  }

  let className = isFirst ? "settings-row settings-row--first" : "settings-row";
  if (isDisabled) className += " settings-row--disabled";
  rowAttrs["data-row-key"] = row.key;

  const listeners: Record<string, EventListener> = {};
  if (activate) {
    listeners.click = () => activate?.();
    listeners.keydown = (ev: Event) => {
      const ke = ev as KeyboardEvent;
      if (ke.key === "Enter" || ke.key === " " || ke.key === "Spacebar") {
        ke.preventDefault();
        activate?.();
        return;
      }
      keyHandler?.(ke);
    };
  } else if (keyHandler) {
    listeners.keydown = (ev: Event) => keyHandler?.(ev as KeyboardEvent);
  }

  return el(
    "div",
    {
      class: className,
      attrs: rowAttrs,
      on: Object.keys(listeners).length > 0 ? listeners : undefined,
    },
    [labelNode, valueNode],
  );
}

function createTogglePill(on: boolean): HTMLElement {
  const knob = el("div", { class: "toggle__knob" });
  return el(
    "div",
    {
      class: on ? "toggle toggle--on" : "toggle",
      attrs: { "aria-hidden": "true" },
    },
    [knob],
  );
}

/**
 * Hand-rolled CSS.escape — happy-dom doesn't ship one and the ids we generate
 * are deterministic ASCII, but the panel's id may be queried with `#<id>`
 * containing hyphens; that's safe. This function exists so the queryselector
 * for stable ids works without pulling in a polyfill.
 */
function cssEscape(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}
